import useFetchGlobalPromptInsights from '@/hooks/useFetchGlobalPromptInsights';
import useFetchGlobalPromptQuestions from '@/hooks/useFetchGlobalPromptQuestions';
import { Box, Button } from '@mui/material';
import { useEffect } from 'react';
import useGenerateInsights from '../hooks/useGenerateInsights';
import InsightsBanner from './banner/InsightsBanner';
import InsightsSummaryCards from './summary/InsightsSummaryCards';
import InsightsLoader from './shared/InsightsLoader';
import { InsightsDateRange } from './insightsTypes';
import QuestionCategorySection from './category/QuestionCategorySection';
import InsightsEmptyState from '@/components/common/InsightsEmptyState';
import ErrorState from '@/components/common/ErrorState';
import DateRangeSelect from '../../common/select/DateRangeSelect';
import { INSIGHT_REGISTRY, INSIGHT_TYPE_ORDER } from './insightsRegistry';
import useFetchInsightsJobStatus from '../hooks/useFetchInsightsJobStatus';
import { Add } from '@mui/icons-material';
import ProjectMultiSelect from '@/components/common/select/ProjectMultiSelect';
import { useDateFormatter } from '@/hooks';
import { isDisabledTestValue } from '@/testFeatures';

/** Time range the insights tab opens on. */
export const DEFAULT_DATE_RANGE: InsightsDateRange = 'last_1_hour';

// Key holding the filter that a running re-run is using.
// Why: if the user refreshes or navigates away mid-run, the tab should come back showing
// the same time range and projects that are being analysed. It is removed once the run
// finishes, so a normal visit always opens on the default: last hour, all projects.
const ACTIVE_FILTER_KEY = 'global-insights-active-filter';

interface StoredFilter {
    dateRange: InsightsDateRange;
    projectIds: number[];
}

/**
 * Reads the filter of a run that was still going when the page was left. Used by the page, which
 * owns the filter because this tab is unmounted whenever the user looks at the Questions tab.
 */
export const readActiveFilter = (): StoredFilter | null => {
    try {
        const raw = localStorage.getItem(ACTIVE_FILTER_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as StoredFilter;
        // A test-only range stored while the switch was on is ignored once it is off, so the
        // default range is used instead of a value the select no longer offers.
        if (isDisabledTestValue(parsed?.dateRange)) {
            return null;
        }
        return parsed?.dateRange && Array.isArray(parsed.projectIds) ? parsed : null;
    } catch {
        return null;
    }
};

interface GlobalPromptInsightsProps {
    /** Switches the parent page back to the Questions tab. */
    onConfigureQuestions?: () => void;
    /**
     * The filter is owned by the page, not by this tab. Switching tabs unmounts the tab, so state
     * kept here would be thrown away every time the user looks at the Questions tab.
     */
    dateRange: InsightsDateRange;
    projectIds: number[];
    onDateRangeChange: (dateRange: InsightsDateRange) => void;
    onProjectIdsChange: (projectIds: number[]) => void;
}

const GlobalPromptInsights: React.FC<GlobalPromptInsightsProps> = ({
    onConfigureQuestions,
    dateRange,
    projectIds,
    onDateRangeChange,
    onProjectIdsChange,
}) => {
    const formatDate = useDateFormatter();

    // Skip loading insights when there are no global prompt questions.
    // The question list is cached and shared with the Questions tab.
    const {
        questions: promptQuestions,
        isLoading: isLoadingQuestions,
        isError: isQuestionsError,
        errorMessage: questionsErrorMessage,
        refetch: refetchQuestions,
    } = useFetchGlobalPromptQuestions();
    const hasPromptQuestions = promptQuestions.length > 0;

    const { insights, isLoading, isFetching, isError, errorMessage, refetch } = useFetchGlobalPromptInsights(
        dateRange,
        projectIds,
        hasPromptQuestions
    );
    // Two different signals, combined so the button spinner never flickers:
    // isStarting  - the POST that starts the run is still going. True the instant it is clicked.
    // isAnalyzing - the polled status says the job is queued or running.
    // Using either on its own leaves a gap between the POST finishing and the first poll.
    const { mutate: generate, isPending: isStarting } = useGenerateInsights();
    const { isAnalyzing, finishedAt, status: jobStatus } = useFetchInsightsJobStatus();
    const isRerunning = isStarting || isAnalyzing;

    // Save the filter only while a run is going, and clear it once the run ends, so next
    // time the tab opens on the default.
    //
    // The clear waits until jobStatus is defined. Why: right after a refresh the status has
    // not been fetched yet, and clearing then would throw away the filter of a run that is
    // actually still going.
    useEffect(() => {
        if (isRerunning) {
            localStorage.setItem(ACTIVE_FILTER_KEY, JSON.stringify({ dateRange, projectIds }));
        } else if (jobStatus !== undefined) {
            localStorage.removeItem(ACTIVE_FILTER_KEY);
        }
    }, [isRerunning, jobStatus, dateRange, projectIds]);

    // Only dim the content while refetching data we already have. On the very first load
    // there is nothing to dim, so the full loader below is used instead.
    const isBusy = isFetching && Boolean(insights);
    const showFilterClearButton = dateRange != DEFAULT_DATE_RANGE || projectIds.length > 0;

    // Shown when the client has no global prompt questions. Used both for the question list
    // coming back empty and for an insights response that carries no questions.
    const noPromptQuestionsState = (
        <InsightsEmptyState
            title="No prompt questions yet"
            description={
                <>
                    Insights are generated from your global prompt questions.Add a question to start seeing AI-analyzed results here.
                    {onConfigureQuestions ? (
                        <Box my={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onConfigureQuestions}
                                sx={{ flexShrink: 0, textTransform: 'none', borderRadius: 2 }}
                                startIcon={<Add />}
                            >
                                Add a question
                            </Button>
                        </Box>
                    ) : (
                        'Questions tab'
                    )}{' '}
                </>
            }
        />
    );

    const renderContent = () => {
        // The question list is still on its way, so it is not known yet whether the insights
        // request will be made. Showing the loader avoids flashing the empty state first.
        if (isLoadingQuestions) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <InsightsLoader label="Loading insights…" />
                </Box>
            );
        }

        // The question list failed, so whether the client has questions is unknown. Reporting
        // the failure is right here; the empty state below would claim there are none.
        if (isQuestionsError) {
            return (
                <ErrorState
                    title="Couldn't load insights"
                    message={questionsErrorMessage || 'Service temporarily unavailable. Please try again.'}
                    onRetry={() => refetchQuestions()}
                />
            );
        }

        // No questions, so no insights request was made. This branch has to come before the
        // ones below: with the request held back there is no data and no loading flag, which
        // would otherwise read as a failure and show the error state.
        if (!hasPromptQuestions) {
            return noPromptQuestionsState;
        }

        // First load, nothing to show yet, so show the loader rather than an empty page.
        // Later refetches keep the old data on screen and dim it with the overlay below.
        if (isLoading && !insights) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <InsightsLoader label="Analyzing insights…" />
                </Box>
            );
        }

        if (isError || !insights) {
            return (
                <ErrorState
                    title="Couldn't load insights"
                    message={errorMessage || 'Service temporarily unavailable. Please try again.'}
                    onRetry={() => refetch()}
                />
            );
        }

        const { summary, questions } = insights;

        // Fallback for the case where the two endpoints disagree, such as the last question
        // being deleted after the cached list was read.
        if (questions.length == 0) {
            return noPromptQuestionsState;
        }

        // Once the client has questions, always show the banner, tiles and sections, even
        // when no calls matched. The tiles then read 0 projects and 0 calls, which is more
        // useful than blanking the whole tab.

        // The sections come from the registry and its type order, so a newly registered
        // question type appears here with no change to this file. See insightsRegistry.
        const sections = INSIGHT_TYPE_ORDER.map((type) => ({
            type,
            questions: questions.filter((question) => question.type === type),
        })).filter((section) => section.questions.length > 0);

        // Sections carry the page, so they are shown whenever the tab can render at least
        // one. A question with no answers for this selection reads "Awaiting insights",
        // which tells the user more than an empty page does, and it keeps every date range
        // behaving the same way.
        //
        // The empty state is only for the case where nothing can be rendered at all: the
        // client's questions are all of a type this tab does not show yet, such as free
        // text. Without this the page went blank, because `sections` was empty and there
        // was no branch for it.
        const hasRenderableSections = sections.length > 0;

        const clearFilters = () => {
            onDateRangeChange(DEFAULT_DATE_RANGE);
            onProjectIdsChange([]);
        };

        return (
            <>
                <InsightsBanner
                    projectCount={summary.projectsAnalyzed}
                    lastUpdatedAt={summary.lastUpdatedAt ? formatDate(summary.lastUpdatedAt) : null}
                    lastRunAt={finishedAt ? formatDate(finishedAt) : null}
                    onRerun={() => generate({ dateRange, projectIds })}
                    onClearFilters={clearFilters}
                    showFilterClearButton={showFilterClearButton}
                    isRerunning={isRerunning}
                    longRunning={isAnalyzing}
                    dateFilter={<DateRangeSelect value={dateRange} onChange={onDateRangeChange} isAnalyzing={isAnalyzing} />}
                    projectFilter={<ProjectMultiSelect value={projectIds} onChange={onProjectIdsChange} isAnalyzing={isAnalyzing} />}
                />

                {/* Only the content below is covered by the loading overlay. The banner with
                    the filters sits above it, so the user can still change them while loading. */}
                <Box sx={{ position: 'relative' }}>
                    {isBusy && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 2,
                                bgcolor: 'rgba(255, 255, 255, 0.65)',
                                borderRadius: '14px',
                            }}
                        >
                            {/* Sticky, so on a long page the loader stays visible near the top
                                instead of sitting off screen in the middle of the overlay. */}
                            <Box sx={{ position: 'sticky', top: 24, display: 'flex', justifyContent: 'center', py: 6 }}>
                                <InsightsLoader label="Loading insights…" />
                            </Box>
                        </Box>
                    )}
                    <InsightsSummaryCards summary={summary} isAnalyzing={isAnalyzing} selectedProjectCount={projectIds.length} />

                    <Box sx={{ mt: 1 }}>
                        {hasRenderableSections ? (
                            sections.map((section, index) => (
                                <QuestionCategorySection
                                    key={section.type}
                                    index={index + 1}
                                    title={INSIGHT_REGISTRY[section.type].title}
                                    questions={section.questions}
                                    combined={insights.combined[section.type]}
                                />
                            ))
                        ) : (
                            <InsightsEmptyState
                                title="Nothing to show for these questions yet"
                                description={
                                    <>
                                        Insights for free-form text questions are not available in this view yet. Add a numeric or yes/no
                                        question to start seeing results here.
                                        {onConfigureQuestions ? (
                                            <Box my={1}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={onConfigureQuestions}
                                                    sx={{ flexShrink: 0, textTransform: 'none', borderRadius: 2 }}
                                                    startIcon={<Add />}
                                                >
                                                    Add a question
                                                </Button>
                                            </Box>
                                        ) : (
                                            ' Use the Questions tab.'
                                        )}
                                    </>
                                }
                            />
                        )}
                    </Box>
                </Box>
            </>
        );
    };

    return <Box>{renderContent()}</Box>;
};

export default GlobalPromptInsights;
